<template>
  <sui-card>
    <sui-card-content class="compact">
      <sui-card-header>
        {{container.name}}
      </sui-card-header>
      <sui-card-meta>
        {{alias}}
      </sui-card-meta>
    </sui-card-content>
    <sui-card-content class="compact" v-if="isHostPage && service" :style="{background}">
      <router-link class="right floated link" :to="`/services/${service.name}`"><Logo class="logo" :entity="service" /></router-link>
      <p>
        Service: <router-link :to="serviceUrl">{{service.name}}</router-link><br>
        Type: {{service.type}}
      </p>
    </sui-card-content>
    <sui-card-content class="compact" v-if="isServicePage && host" :style="{background}">
      <router-link class="right floated link" :to="hostUrl"><Logo class="logo" :entity="host" /></router-link>
      <p>
        Host: <router-link :to="hostUrl">{{host.name}}</router-link>
        <span v-if="nextHost && isMoving"> ⇒ <router-link :to="nextHostUrl">{{nextHost.name}}</router-link></span><br>
        IP:
        <span v-if="host.ipAddress">
          <a :href="endpoint" target="_blank" v-if="endpoint">{{host.ipAddress}}</a>
          <span v-if="!endpoint">{{host.ipAddress}}</span>
          &nbsp;
          <CopyIcon :value="host.ipAddress" />
        </span>
        <span v-if="!host.ipAddress">
          Not defined
        </span>
      </p>
    </sui-card-content>
    <sui-card-content class="destroyed" v-if="isHostPage && !service">
      The service of this container is destroyed<span v-show="!member.isOwner"> or you don't have permissions to see it</span>.
    </sui-card-content>
    <sui-card-content class="destroyed" v-if="isServicePage && !host">
      The host of this container is destroyed<span v-show="!member.isOwner"> or you don't have permissions to see it</span>.
    </sui-card-content>
    <sui-card-content>
      <span v-if="!hasStats">No stats yet.</span>
      <Stats :entity="container" v-if="hasStats" />
    </sui-card-content>
    <sui-button-group class="attached" size="tiny" fluid v-if="!isMoving">
      <sui-button :disabled="isOffline || !actions.serviceTerminal" data-goal="CONTAINER_CARD_TERMINAL" @click.stop.prevent="$modals.terminal.open(container)">Terminal</sui-button>
      <sui-button :disabled="!actions.serviceInfo" data-goal="CONTAINER_CARD_METRICS" @click.stop.prevent="$modals.metrics.open(container)">Metrics</sui-button>
      <sui-button :disabled="!actions.serviceInfo" data-goal="CONTAINER_CARD_LOGS" @click.stop.prevent="$modals.log.open(container)">Logs</sui-button>
      <sui-dropdown class="icon" icon="ellipsis vertical" button :disabled="inProgress">
        <sui-dropdown-menu class="">
          <sui-dropdown-item icon="share square" text="Move" :disabled="!actions.serviceMoveContainer" data-goal="CONTAINER_CARD_MOVE" @click.stop="$modals.moveContainer.open(container)" />
          <sui-dropdown-item icon="terminal" text="Execute" :disabled="!actions.serviceTerminal" data-goal="CONTAINER_CARD_EXECUTE" @click.stop="$modals.executeCommands.open(container)" />
          <sui-dropdown-divider />
          <sui-dropdown-item icon="trash" text="Destroy" :disabled="!actions.serviceScale" data-goal="CONTAINER_CARD_DESTROY" @click.stop="destroy()" />
          <sui-dropdown-item icon="stop" text="Stop" :disabled="!actions.serviceUpdate" data-goal="CONTAINER_CARD_STOP" v-show="!isOffline" @click.stop="stop()" />
          <sui-dropdown-item icon="refresh" text="Restart" :disabled="!actions.serviceUpdate" data-goal="CONTAINER_CARD_RESTART" v-show="!isOffline" @click.stop="restart()" />
          <sui-dropdown-item icon="play" text="Start" :disabled="!actions.serviceUpdate" data-goal="CONTAINER_CARD_START" v-show="isOffline" @click.stop="start()" />
        </sui-dropdown-menu>
      </sui-dropdown>
    </sui-button-group>
    <sui-button-group class="attached" size="tiny" fluid v-if="isMoving">
      <sui-button color="green" :disabled="!actions.serviceMoveContainer" @click.stop.prevent="confirmMoving()">Confirm</sui-button>
      <sui-button color="red" :disabled="!actions.serviceMoveContainer" @click.stop.prevent="cancelMoving()">Cancel</sui-button>
    </sui-button-group>
    <EntityStatus :entity="container" />
  </sui-card>
</template>

<script>
  import {mapGetters} from 'vuex'
  import Stats from '@/components/Stats'
  import EntityStatus from '@/components/EntityStatus'
  import utils from '@/utils'

  export default {
    name: 'ContainerCard',
    components: {
      Stats,
      EntityStatus,
    },
    props: {
      container: {
        type: Object,
        required: true,
      },
    },
    computed: {
      ...mapGetters(['actionsById', 'config', 'hostsById', 'member']),
      actions() {
        return this.actionsById[this.container.service] || {}
      },
      isOffline() {
        return this.container._isOffline
      },
      inProgress() {
        return this.container._inProgress
      },
      isMoving() {
        return this.container._isMoving
      },
      isHostPage() {
        return this.$route.path.match(/^\/hosts\/\w+/)
      },
      isServicePage() {
        return this.$route.path.match(/^\/services\/\w+/)
      },
      service() {
        return this.container._service
      },
      host() {
        return this.container._host
      },
      nextHost() {
        return this.container.changes && this.container.changes.host ? this.hostsById[this.container.changes.host] : null
      },
      hostUrl() {
        return this.host._path
      },
      nextHostUrl() {
        return this.nextHost._path
      },
      serviceUrl() {
        return this.service._path
      },
      alias() {
        return this.container._alias
      },
      endpoint() {
        return this.container._endpoint
      },
      background() {
        if (this.config.colorCards && this.isHostPage && this.service) {
          return `linear-gradient(135deg, transparent, ${this.service.constructor.color}22)`
        }
        if (this.config.colorCards && this.isServicePage && this.host) {
          return `linear-gradient(135deg, transparent, ${this.host.constructor.color}22)`
        }
        return 'transparent'
      },
      hasStats() {
        return Boolean(this.container._stats)
      },
      hasParents() {
        return Boolean(this.service && this.host)
      },
    },
    methods: {
      confirmMoving() {
        this.container.moveConfirm().catch(() => {})
      },
      cancelMoving() {
        this.container.moveCancel().catch(() => {})
      },
      start() {
        this.container.start().catch(() => {})
      },
      restart() {
        return this.$modals.confirm.open({
          callback: () => {
            this.container.restart().catch(() => {})
          },
        })
      },
      stop() {
        return this.$modals.confirm.open({
          callback: () => {
            this.container.stop().catch(() => {})
          },
        })
      },
      destroy() {
        if (this.service && this.service._containers.length === 1) {
          return this.$modals.confirm.open({
            text: `This is the last container of the service, so the service <b>${this.service.name}</b> will be destroyed too.`,
            callback: () => {
              this.service.destroy().catch(() => {})
              this.service._project.openPage()
            },
          })
        }
        return this.$modals.confirm.open({
          callback: () => {
            utils.reachGoal('CONTAINER_CARD_DESTROY_TRY')
            this.container.destroy().then(() => {
              utils.reachGoal('CONTAINER_CARD_DESTROY_SUCCESS')
            }).catch(() => {
              utils.reachGoal('CONTAINER_CARD_DESTROY_FAIL')
            })
          },
        })
      },
    },
  }
</script>

<style scoped>
  .logo { margin: 0!important; height: 3.5em; }
  .link.right.floated { margin: -.4em!important; }
  .link.dropdown.icon { flex: 0!important; border-radius: 0!important; }
  .button.dropdown { flex-grow: 0!important; border-radius: 0!important; }
  .destroyed { display: flex; align-items: center; justify-content: center; min-height: 4em; }
</style>
